import { Component, inject, input, OnInit, output, viewChild } from '@angular/core';
import { CustomerService } from '../../services/customer.service';
import { QuantityControlComponent } from '../quantity-control/quantity-control.component';
import { OrderItemDto } from '../../dtos/order-item.dto';
import { finalize } from 'rxjs/operators';
import { DEFAULT_ERROR_TEXT, MINIMAL_ORDER_COST } from '../../constants';
import { GoogleAnalyticsService } from '../../services/google-analytics.service';
import { environment } from '../../../environments/environment';
import { NgClass } from '@angular/common';
import { RouterLinkWithLang } from '../../directives/router-link-with-lang/router-link-with-lang.directive';
import { CrossSellProductsComponent } from './components/cross-sell-products/cross-sell-products.component';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss'],
  imports: [QuantityControlComponent, NgClass, CrossSellProductsComponent, TranslatePipe, RouterLink, RouterLinkWithLang],
})
export class CartComponent implements OnInit {
  private customerService = inject(CustomerService);
  private analyticsService = inject(GoogleAnalyticsService);


  uploadedHost = environment.uploadedHost;
  isLoading: boolean = false;
  cartError: string | null = null;
  minimalOrderCost = MINIMAL_ORDER_COST;
  private cartErrorTimeout: number | undefined;

  get items() { return this.customerService.cart; }
  get prices() { return this.customerService.prices; }
  get canPlaceOrder() { return this.prices.totalCost >= this.minimalOrderCost; }
  get itemsCost(): number { return this.customerService.prices.itemsCost; }

  readonly isCrossSellVisible = input<boolean>(false);
  readonly continueShoppingEvt = output({ alias: 'continueShopping' });
  readonly qtyCmp = viewChild(QuantityControlComponent);

  ngOnInit(): void {
  }

  onQtyChange(item: OrderItemDto, qty: number) {
    this.resetCartError();
    this.isLoading = true;
    this.customerService.updateQtyInCart(item, qty)
      .pipe( finalize(() => this.isLoading = false) )
      .subscribe(
        _ => { },
        error => {
          this.qtyCmp().setValue(item.qty, false);
          this.setCartError(error);
        }
      );
  }

  deleteFromCart(item: OrderItemDto) {
    this.customerService.deleteFromCart(item.sku).subscribe();
    this.resetCartError();
    this.analyticsService.removeFromCart(item.sku, item.name, item.price, item.qty);
  }

  checkout() {
    this.resetCartError();
  }

  continueShopping() {
    this.continueShoppingEvt.emit();
  }

  private setCartError(error) {
    clearTimeout(this.cartErrorTimeout);

    this.cartError = error.error?.message || DEFAULT_ERROR_TEXT;
    this.cartErrorTimeout = setTimeout(() => this.resetCartError(), 10000) as any;
  }

  private resetCartError() {
    this.cartError = null;
    clearTimeout(this.cartErrorTimeout);
  }

  setItemImg(item) {
    if (!item.imageUrl) {
      return '/assets/images/no-img.jpg';
    } else {
      return this.uploadedHost + item.imageUrl;
    }
  }

  getActivePriceIndex(product: OrderItemDto): number {
    let activePriceIndex;

    for (let i = product.discountedPrices.length - 1; i >= 0; i--) {
      if (this.itemsCost >= product.discountedPrices[i].costBreakpoint && !activePriceIndex) {
        activePriceIndex = i;
      }

      if (product.price >= product.discountedPrices[i].costBreakpoint && !activePriceIndex) {
        activePriceIndex = i;
      }
    }

    return activePriceIndex;
  }

  getItemCostWithDiscount(product: OrderItemDto): number {
    if (this.getActivePriceIndex(product) === undefined) { return; }

    const discountActivePrice = product.discountedPrices[this.getActivePriceIndex(product)].price;
    return product.qty * discountActivePrice;
  }
}
